import platform from 'platform';
import { breakpointSizes, breakpointLandscapeSizes } from './media-handler';
import { browsersFeatureCheck, supportedBrowsers } from './supported-browsers';

/* iOS user agent examples
  newer iPads (iOS 13.1) - Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_4) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1 Safari/605.1.15
  older iPads (iOS 12.1.4) - Mozilla/5.0 (iPad; CPU OS 12_1_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/12.0 Mobile/15E148 Safari/604.1
  iPhone - "Mozilla/5.0 (iPhone; CPU iPhone OS 14_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/14.0.3 Mobile/15E148 Safari/604.1"
*/
const newerIpadIosVersionPattern = /(Version\/)(\d+)/;
const olderIpadIosVersionPattern = /(OS )(\d+)/;
const iphoneIosVersionPattern = olderIpadIosVersionPattern;

const getPlatformOS = () => {
  return platform.parse(window.navigator.userAgent).os;
};

export const getSafariVersionInferredFromFeatureCheck = () => {
  // if version returned from ua is 0 then its most likely a mobile app so check the feature
  const versionInferredFromFeatureCheck = Number(
    Object.entries(browsersFeatureCheck.Safari)
      .sort(([a], [b]) => +b - +a)
      .find(([, featureCheck]) => featureCheck())?.[0]
  );

  if (versionInferredFromFeatureCheck) return versionInferredFromFeatureCheck;

  return 0;
};

export const getIosVersion = (): number => {
  const userAgent = window.navigator.userAgent;
  const iOSPattern = isIpad()
    ? userAgent.includes('CPU OS')
      ? olderIpadIosVersionPattern
      : newerIpadIosVersionPattern
    : iphoneIosVersionPattern;
  const match = iOSPattern.exec(userAgent);
  const versionInferredFromUA = Array.isArray(match) && !!parseInt(match[2]) ? parseInt(match[2]) : 0;

  if (versionInferredFromUA !== 0) {
    return versionInferredFromUA;
  }

  return getSafariVersionInferredFromFeatureCheck();
};

export const isSupportedIosVersion = () =>
  !isIos() || getIosVersion() >= supportedBrowsers.Safari.oldestSupportedVersion;

export const isIos = (): boolean => {
  return /(iphone|ipod|ipad)/i.test(window.navigator.userAgent) || isIpad();
};

export const isIphone = (): boolean => {
  // We check that this isn't an iPad and if it is an iPhone because
  // Facebook's UIWebView on iPad also identifies itself as an iPhone.
  // See http://artsy.github.io/blog/2012/10/18/the-perils-of-ios-user-agent-sniffing/
  return !isIpad() && /iphone/i.test(window.navigator.userAgent);
};

export const isIpad = (): boolean => {
  return (
    !isLegacyEdge() &&
    (/ipad/i.test(window.navigator.userAgent) ||
      !!(navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform)))
  );
};

export const isIosSafari = (): boolean => {
  return isSafariWebView() || (isIos() && /version/i.test(window.navigator.userAgent));
};

export const isSafariWebView = () => {
  return /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(window.navigator.userAgent);
};

export const isSafari = (): boolean => {
  return isSafariWebView() || /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
};

export const isIosChrome = (): boolean => {
  return isIos() && /crios/i.test(window.navigator.userAgent);
};

export const isLessThanIos6 = (): boolean => {
  return isIos() && getIosVersion() > 0 && getIosVersion() < 6;
};

export const isOpera = (): boolean => {
  return window.navigator.userAgent.indexOf('OPR') > -1 || window.navigator.userAgent.indexOf('Opera') > -1;
};

export const isNativeAndroid = (): boolean => {
  return /android(?!.*(chrome|firefox))/i.test(window.navigator.userAgent);
};

export const isAndroidKitKat = (): boolean => {
  return /(android 4\.4)/i.test(window.navigator.userAgent);
};

/**
 * @deprecated
 * IE11 is not supported anymore
 */
export const isIE9 = (): boolean => {
  const html = window.document.querySelector('html');
  return !!html && /\bie9\b/.test(html.className);
};

/**
 * @deprecated
 * IE11 is not supported anymore
 */
export const isIElte10 = (): boolean => {
  return window.navigator.userAgent.includes('MSIE');
};

/**
 * @deprecated
 * IE11 is not supported anymore
 */
export const isIE11 = (): boolean => {
  return !window.navigator.userAgent.includes('MSIE') && window.navigator.userAgent.includes('Trident');
};

/**
 * @deprecated
 * IE11 is not supported anymore
 */
export const isIElte11 = (): boolean => {
  return isIE11() || isIElte10();
};

/**
 * @deprecated
 * IE11 is not supported anymore
 */
export const isIE = (): boolean => {
  return isIElte10() || isIE11() || isIE9();
};

export const isLegacyEdge = (): boolean => /Edge/.test(window.navigator.userAgent);

export const isNewEdge = (): boolean => !isLegacyEdge() && /Edg/.test(window.navigator.userAgent);

export const isBlackBerry = (): boolean => {
  return /blackberry/i.test(window.navigator.userAgent);
};

export const isWindowsPhone = (): boolean => {
  return /iemobile/i.test(window.navigator.userAgent);
};

export const isMobile = (): boolean => {
  return isAndroid() || isBlackBerry() || isIphone() || isWindowsPhone();
};

export const isIos8 = (): boolean => {
  return isIos() && /os 8/i.test(window.navigator.userAgent);
};

/**
 * Checks if user agent IOS version is less than 8.0.2
 */
export const isIosLt802 = (): boolean => {
  return isIos() && /(os 8_0 )|(os 8_0_1)/i.test(window.navigator.userAgent);
};

export const isIos13orBelow = (): boolean => {
  return isIos() && getIosVersion() > 0 && getIosVersion() < 14;
};

export const isIos12orBelow = (): boolean => {
  return isIos() && getIosVersion() > 0 && getIosVersion() < 13;
};

export const isIos14orBelow = (): boolean => {
  return isIos() && getIosVersion() > 0 && getIosVersion() < 15;
};

export const isIos15orBelow = (): boolean => {
  return isIos() && getIosVersion() > 0 && getIosVersion() < 16;
};

export const isAndroidKitKat442OrBelow = (): boolean => {
  const os = getPlatformOS();
  if (os) {
    const specificVersion = os.version;
    return (
      isAndroidKitKat() && (specificVersion === '4.4.2' || specificVersion === '4.4.1' || specificVersion === '4.4')
    );
  }
  return false;
};

export const isAndroid = (): boolean => {
  const os = getPlatformOS();
  return !!os && os.family === 'Android';
};

export const isLargeAndroid = () => isAndroid() && window.matchMedia('(min-width: 600px)').matches;

export const isMacOSX = (): boolean => {
  const os = getPlatformOS();
  return !!os && os.family === 'OS X';
};

export const isChrome = (): boolean => (platform.parse(window.navigator.userAgent).name ?? '').includes('Chrome');

export const isWindowsOS = (): boolean => {
  const os = getPlatformOS();
  return !!os && !!os.family?.includes('Windows');
};

export const isDesktopSafari = () => isMacOSX() && isSafari();

export const isFirefox = () => /firefox/i.test(window.navigator.userAgent);

export const isDevice = () => isMobile() || isIpad();

export const isPortraitMode = () => window.matchMedia('(orientation: portrait)').matches;

export const isLandscapeMode = () => window.matchMedia('(orientation: landscape)').matches;

export const isMobileLandscape = () => window.matchMedia(mediaQueries.isMobileLandscape).matches;

export const isSmallDesktop = () => window.matchMedia('(max-width: 920px)').matches;

/**
 * @deprecated
 * Use queries from @common/theme/helpers/media-queries or add new to @common/theme/mixins/media-queries
 */
export const mediaQueries = {
  isPortrait: '(orientation: portrait)',
  isMobileLandscape: '(max-width: 926px) and (max-height: 428px) and (orientation: landscape)',
  isIphone5Portrait: '(max-width: 320px) and (max-height: 568px) and (orientation: portrait)',
  isIphone5Landscape: '(max-width: 568px) and (max-height: 320px) and (orientation: landscape)',
  isSmallMobilePortrait: '(max-width: 375px) and (max-height: 667px) and (orientation: portrait)',
  isMobilePortraitWithNoAspectRatio: '(max-width: 432px) and (max-height: 864px) and (orientation: portrait)',
  isMobilePortrait:
    '(max-width: 432px) and (max-height: 864px) and (orientation: portrait), (max-width: 432px) and (max-aspect-ratio: 13/9)',
  isTabletPortrait:
    '(min-width: 414px) and (max-width: 1024px) and (min-height: 812px) and (max-height: 1366px) and (orientation: portrait)',
  isMediumTabletPortrait:
    '(min-width: 768px) and (max-width: 1024px) and (min-height: 812px) and (max-height: 1366px) and (orientation: portrait)',
  isSmallTablet: '(min-width: 414px) and (max-width: 768px) and (min-height: 609px) and (max-height: 1024px)',
  isTabletLandscape: '(max-width: 1024px) and (min-height: 768px) and (orientation: landscape)',
  isBigTabletLandscape: '(max-width: 1366px) and (min-height: 1024px) and (orientation: landscape)',
  isBigTabletPortrait: '(min-width: 1024px) and (max-height: 1366px) and (orientation: portrait)',
  isDefaultEmbedSize: '(max-width: 650px) and (max-height: 400px) and (min-width: 650px) and (min-width: 400px)',
  isEmbed: '(max-width: 650px) and (max-height: 400px)',
  isSmallEmbed: '(max-width: 550px) and (max-height: 350px) and (orientation: landscape)',
  isSmallMobileLandscape: '(max-width: 667px) and (max-height: 375px) and (orientation: landscape)',
  isZoomed200Landscape: '(max-width: 720px) and (max-height: 412px) and (orientation: landscape)',
  isZoomed300Landscape: '(max-width: 520px) and (max-height: 310px) and (orientation: landscape)',
  isSmallDesktop: '(max-width: 1280px)',
  isMediumDesktop: '(max-width: 600px)',
  isLargeMediumDesktop: '(max-width: 920px)',
  isLargeDesktop: '(min-width: 1441px) and (min-height: 900px)',
  isLargeBreakpoint: `(min-width: ${breakpointSizes.large}px)`,
  isDownOrEqualLargeBreakpoint: `(max-width: ${breakpointSizes.large}px)`,
  reduceMotion: '(prefers-reduced-motion: reduce)',
  isShortDesktop: '(max-height: 640px)',
  isLaptop: '(min-width: 1200px)',
  isSmallDashboard: '(max-width: 400px)',
  isSmallMediumBreakpoint: `(min-width: ${breakpointSizes.smallMedium}px)`,
  isMediumBreakpoint: `(min-width: ${breakpointSizes.medium}px)`,
  isPhoneLandscapeBreakpoint: `(min-width: ${breakpointSizes.phoneLandscape}px)`,
  isSmallMediumLandscapeBreakpoint: `(min-width: ${breakpointLandscapeSizes.smallMedium}px)`,
  isDownOrEqualSmallMediumLandscapeBreakpoint: `(max-width: ${breakpointLandscapeSizes.smallMedium}px)`,
  isBetweenMediumAndLargeBreakpoint: `(min-width: ${breakpointSizes.medium}px) and (max-width: ${
    breakpointSizes.large - 0.02
  }px)`,
  isColumnLayoutBreakpoint: `(min-width: ${breakpointSizes.columnLayoutWidth}px)`,
  isDownOrEqualSmallTablet: `(max-width: ${breakpointSizes.columnLayoutWidth}px)`,
};

export const prefersReducedMotion = () => window.matchMedia(mediaQueries.reduceMotion).matches;

export const supportsAnimation = () => !prefersReducedMotion() && !isIElte11() && !isLegacyEdge();

const checkIfRecentiOSVersion = (os: string) => {
  try {
    const browserVersion = parseInt(os.split(' ')[1].split('.')[0]);
    const { oldestSupportedVersion } = supportedBrowsers.Safari;

    return browserVersion >= oldestSupportedVersion;
  } catch {
    return false;
  }
};

export const isBrowserSupported = () => {
  if (isPlaywright()) {
    return true;
  }
  const browser = platform.name || '';
  const osString = platform.os?.toString();

  if (isZoom() || isPingdom()) {
    return true;
  }
  if (osString && osString.includes('iOS')) {
    return checkIfRecentiOSVersion(osString);
  } else {
    const version = parseInt(platform.version || '');
    const support = supportedBrowsers[browser];
    if (!!support && !!version) {
      return version >= support.oldestSupportedVersion;
    }
    return false;
  }
};

export const isTouchDevice = () =>
  'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.maxTouchPoints > 0;

export const isPlaywright = () => !!window?.navigator?.webdriver;

export const isSoftwareKeyboardActive = () => {
  if (isTouchDevice()) {
    const focusedElement = document.activeElement;
    const tagNameRegex = /(textarea|input)/;

    return !!tagNameRegex.exec(focusedElement?.tagName.toLowerCase() ?? '');
  }
  return false;
};

export const supportsFullscreen = () => {
  if (isIpad()) {
    // Fullscreen support was added for iPads in iOS 16.4, but it doesn't work well.
    // According to MDN, it is "unsuitable for some use cases like games", which is why we disable it for all practical purposes.
    return false;
  }
  for (const key of [
    'exitFullscreen',
    'webkitExitFullscreen',
    'webkitCancelFullScreen',
    'mozCancelFullScreen',
    'msExitFullscreen',
  ]) {
    if (key in document) {
      return true;
    }
  }
  return false;
};

/**
 * We cannot rely only on user agent as Zoom unfortunately breaks this behavior every now and then.
 * To remedy this for the time being we check if the Zoom app is wrapping the platform by
 * looking at the referrer and whether we're currently in an iFrame.
 * @returns a boolean letting us know whether we think this is Zoom or not
 */

export const isZoom = () => {
  const isIframeOnZoomDomain = document.referrer.includes('kazoom') && window.self !== window.top;

  return isIframeOnZoomDomain || /zoomapps/i.test(window.navigator.userAgent);
};

/**
 * We want to use pingdom to test our sites.
 * Platform doesn't really parse the pingdom user agent properly,
 * so this function is used to make an exception.
 *
 * See https://mobitroll.atlassian.net/browse/KAHOOT-25511
 */
export const isPingdom = () => {
  return window.navigator.userAgent.includes('PingdomTMS');
};

export const isAppleDevice = () => isMacOSX() || isIos();

export default {
  isAndroidKitKat442OrBelow,
  isIosLt802,
  isIos8,
  isMacOSX,
  isAndroid,
  isWindowsOS,
  getIosVersion,
  isIE,
  isIElte11,
  isBlackBerry,
  isWindowsPhone,
  isMobile,
  isOpera,
  isIphone,
  isIE9,
  isLessThanIos6,
  isIpad,
  isIosSafari,
  isIosChrome,
  isIElte10,
  isNativeAndroid,
  isAndroidKitKat,
  isPortraitMode,
  isLandscapeMode,
  isFirefox,
};
